/**
 * Monaco language definition for the Pine subset this engine runs.
 *
 * The completion list is built from the engine's own /pine/reference endpoint
 * rather than hard-coded, so the editor cannot suggest a function the
 * interpreter does not implement.
 */
import type { Monaco } from "@monaco-editor/react";
import type { IPosition, editor, languages } from "monaco-editor";

const KEYWORDS = [
  "if", "else", "for", "to", "by", "while", "var", "varip", "and", "or", "not",
  "true", "false", "na", "break", "continue", "switch", "import", "export",
];

const TYPES = [
  "int", "float", "bool", "string", "color", "series", "simple", "const",
];

export function registerPine(monaco: Monaco): void {
  const id = "pine";
  const known = monaco.languages.getLanguages() as languages.ILanguageExtensionPoint[];
  if (known.some((l) => l.id === id)) return;

  monaco.languages.register({ id });

  monaco.languages.setMonarchTokensProvider(id, {
    keywords: KEYWORDS,
    typeKeywords: TYPES,
    namespaces: [
      "ta", "math", "str", "color", "input", "strategy", "syminfo", "timeframe",
      "barstate", "request", "plot", "shape", "location", "size", "display",
      "format", "line", "label", "alert", "array", "session", "dayofweek",
    ],
    builtins: [
      "open", "high", "low", "close", "volume", "hl2", "hlc3", "ohlc4", "hlcc4",
      "time", "bar_index", "last_bar_index", "year", "month", "dayofmonth",
      "dayofweek", "hour", "minute", "second", "nz", "fixnan", "plot",
      "plotshape", "plotchar", "hline", "fill", "bgcolor", "barcolor",
      "indicator", "strategy", "alertcondition", "alert", "timestamp",
    ],
    tokenizer: {
      root: [
        [/\/\/.*$/, "comment"],
        [/#[0-9a-fA-F]{6,8}\b/, "number.hex"],
        [/"([^"\\]|\\.)*"/, "string"],
        [/'([^'\\]|\\.)*'/, "string"],
        [/\b\d+\.?\d*([eE][+-]?\d+)?\b/, "number"],
        [/\b(ta|math|str|color|input|strategy|syminfo|timeframe|barstate|request|array|label|line)\./, "type.identifier"],
        [/:=/, "operator"],
        [/=>/, "operator"],
        [/[=!<>]=?/, "operator"],
        [/[+\-*/%?:]/, "operator"],
        [
          /[a-zA-Z_]\w*/,
          {
            cases: {
              "@keywords": "keyword",
              "@typeKeywords": "keyword.type",
              "@builtins": "variable.predefined",
              "@default": "identifier",
            },
          },
        ],
      ],
    },
  });

  monaco.languages.setLanguageConfiguration(id, {
    comments: { lineComment: "//" },
    brackets: [["(", ")"], ["[", "]"]],
    autoClosingPairs: [
      { open: "(", close: ")" },
      { open: "[", close: "]" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
    // Pine blocks are indentation-based, like Python.
    onEnterRules: [
      {
        beforeText: /^\s*(if|else|for|while|switch)\b.*$/,
        action: { indentAction: monaco.languages.IndentAction.Indent },
      },
      {
        beforeText: /=>\s*$/,
        action: { indentAction: monaco.languages.IndentAction.Indent },
      },
    ],
  });

  monaco.editor.defineTheme("tt-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "5a6474", fontStyle: "italic" },
      { token: "keyword", foreground: "c77dff" },
      { token: "keyword.type", foreground: "5ad8f0" },
      { token: "type.identifier", foreground: "d9a13b" },
      { token: "variable.predefined", foreground: "2dd4a7" },
      { token: "string", foreground: "a3d977" },
      { token: "number", foreground: "ff9f6e" },
      { token: "number.hex", foreground: "ff9f6e" },
      { token: "operator", foreground: "99a3b5" },
    ],
    colors: {
      "editor.background": "#0b0e14",
      "editor.foreground": "#e3e8f0",
      "editorLineNumber.foreground": "#3c4658",
      "editorLineNumber.activeForeground": "#99a3b5",
      "editor.lineHighlightBackground": "#121620",
      "editor.selectionBackground": "#23375e",
      "editorCursor.foreground": "#4d8dff",
      "editorIndentGuide.background1": "#1a1f2b",
    },
  });

  /** Light counterpart. Picked to match the TradingView-style light palette
   *  the rest of the app uses so flipping the theme reads as one page, not
   *  "dark app with a white Pine window glued on". */
  monaco.editor.defineTheme("tt-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "comment", foreground: "8792a4", fontStyle: "italic" },
      { token: "keyword", foreground: "7c3aed" },
      { token: "keyword.type", foreground: "0ea5b7" },
      { token: "type.identifier", foreground: "b45309" },
      { token: "variable.predefined", foreground: "0e9d7b" },
      { token: "string", foreground: "4b7f16" },
      { token: "number", foreground: "b45309" },
      { token: "number.hex", foreground: "b45309" },
      { token: "operator", foreground: "5d6475" },
    ],
    colors: {
      "editor.background": "#ffffff",
      "editor.foreground": "#131722",
      "editorLineNumber.foreground": "#aab1c0",
      "editorLineNumber.activeForeground": "#5d6475",
      "editor.lineHighlightBackground": "#f1f3f8",
      "editor.selectionBackground": "#d6e1ff",
      "editorCursor.foreground": "#2962ff",
      "editorIndentGuide.background1": "#e8ecf4",
    },
  });
}

/** Register completions from the engine's real capability list. */
export function registerCompletions(
  monaco: Monaco,
  reference: { functions: Record<string, string[]>; variables: string[]; constants: string[] },
): void {
  return void monaco.languages.registerCompletionItemProvider("pine", {
    triggerCharacters: ["."],
    provideCompletionItems: (model: editor.ITextModel, position: IPosition) => {
      const word = model.getWordUntilPosition(position);
      const suggestions: languages.CompletionItem[] = [];
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };
      for (const sigs of Object.values(reference.functions)) {
        for (const sig of sigs) {
          const name = sig.slice(0, sig.indexOf("("));
          const params = sig.slice(sig.indexOf("(") + 1, sig.lastIndexOf(")"));
          suggestions.push({
            label: sig,
            kind: monaco.languages.CompletionItemKind.Function,
            insertText: params
              ? `${name}(${params.split(", ").map((p, i) => `\${${i + 1}:${p}}`).join(", ")})`
              : `${name}()`,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            filterText: name,
            detail: "function",
            range,
          });
        }
      }
      for (const v of [...reference.variables, ...reference.constants]) {
        suggestions.push({
          label: v,
          kind: monaco.languages.CompletionItemKind.Constant,
          insertText: v,
          detail: "built-in",
          range,
        });
      }
      return { suggestions };
    },
  });
}

export const STARTER_INDICATOR = `//@version=5
indicator("My indicator", overlay=true)

fastLen = input.int(20, "Fast", minval=1)
slowLen = input.int(50, "Slow", minval=2)
src     = input.source(close, "Source")

fast = ta.ema(src, fastLen)
slow = ta.ema(src, slowLen)

plot(fast, "Fast EMA", color=color.blue, linewidth=2)
plot(slow, "Slow EMA", color=color.orange, linewidth=2)

plotshape(ta.crossover(fast, slow),  style=shape.triangleup,
          location=location.belowbar, color=color.green, title="Cross up")
plotshape(ta.crossunder(fast, slow), style=shape.triangledown,
          location=location.abovebar, color=color.red,   title="Cross down")
`;

export const STARTER_STRATEGY = `//@version=5
strategy("My strategy", overlay=true, initial_capital=100000)

fastLen = input.int(20, "Fast", minval=1)
slowLen = input.int(50, "Slow", minval=2)
useStop = input.bool(true, "Use ATR stop")
atrMult = input.float(2.0, "ATR multiple", minval=0.5, step=0.5)

fast = ta.ema(close, fastLen)
slow = ta.ema(close, slowLen)
atr  = ta.atr(14)

plot(fast, "Fast", color=color.blue)
plot(slow, "Slow", color=color.orange)

if ta.crossover(fast, slow)
    strategy.entry("L", strategy.long)

if useStop
    strategy.exit("X", from_entry="L", stop=close - atrMult * atr)

if ta.crossunder(fast, slow)
    strategy.close("L")
`;
